import type { EntityPlanAttestation } from '@verifime/components';
import { useState } from 'react';
import type { TConfirmEntityPlanState } from '../confirmEntityPlanState';
import { attestationLabelFor, footerNoteFor } from '../planAttestation';
import { useHeldWhile } from './useHeldWhile';

type TPlanAttestation = {
  attestation?: EntityPlanAttestation;
  footerNote?: string;
};

/**
 * The tick for the statement `label`, held in view state and unticked on every mount. It belongs to
 * the statement it was given for: as soon as the plan says anything else (another entity, another
 * name, or no attestation at all) the tick is cleared, so a statement that comes back is never
 * ticked until the person ticks it again.
 */
function useTickFor(label: string | undefined) {
  const [attestedLabel, setAttestedLabel] = useState<string>();
  if (attestedLabel !== undefined && attestedLabel !== label) {
    setAttestedLabel(undefined);
  }
  return {
    checked: attestedLabel === label,
    onChange: (checked: boolean) => setAttestedLabel(checked ? label : undefined),
  };
}

/**
 * The attestation and the footer note for the plan on the confirm screen.
 *
 * While a share runs the grants refresh what is shared, which can turn the plan into "Back to home"
 * under the person's hand. The statement and the footer they ticked and read stay as they were until
 * the share ends or the screen is left.
 */
export function usePlanAttestation(
  currentState: TConfirmEntityPlanState,
  isSharing: boolean,
): TPlanAttestation {
  const state = useHeldWhile(currentState, isSharing);
  const plan = state.status === 'loaded' ? state.plan : undefined;
  const label = plan && attestationLabelFor(plan);
  const tick = useTickFor(label);
  if (!plan) {
    return {};
  }
  const footerNote = footerNoteFor(plan);
  return label === undefined ? { footerNote } : { attestation: { label, ...tick }, footerNote };
}
